/**
 * Catálogo de mensagens do i18next — PT/EN/ES.
 *
 * Estrutura escalável: o "núcleo" (casca da conta, preferências, navegação, mapa,
 * comuns) fica aqui, à mão. Cada TELA traduzida tem um dicionário co-locado
 * `<Nome>.i18n.ts` exportando `{ pt, en, es }`, coletado por `import.meta.glob`.
 * Assim traduzir várias telas em paralelo nunca conflita neste arquivo central.
 *
 * `t(key)` (ou `t(key, params)` com interpolação `{{nome}}`) resolve no idioma atual,
 * caindo para PT e, por fim, para a própria chave. Chaves são strings livres
 * (namespace por tela, ex.: `dashboard.kpi.requested`); um typo cai no fallback.
 */

export type Lang = 'pt' | 'en' | 'es'

export const DEFAULT_LANG: Lang = 'pt'
export const LANG_STORAGE_KEY = 'transgraos.lang'

/** Código do idioma → atributo `lang` do documento (acessibilidade/hifenização). */
export const HTML_LANG: Record<Lang, string> = { pt: 'pt-BR', en: 'en', es: 'es' }

/** Código do idioma → locale do dayjs (@mui/x-date-pickers). */
export const DAYJS_LOCALE: Record<Lang, string> = { pt: 'pt-br', en: 'en', es: 'es' }

/** Chave de tradução (string livre com namespace por tela). */
export type MessageKey = string

type Dict = Record<string, string>
type LocaleBundle = Record<Lang, Dict>

// ── Núcleo (mantido à mão) ───────────────────────────────────────────
const core: LocaleBundle = {
  pt: {
    // Conta / menu do avatar
    'account.settings': 'Configurações',
    'account.logout': 'Sair',
    'account.role.staff': 'Backoffice',
    'account.role.client': 'Cliente',
    // Preferências
    'prefs.theme': 'Tema',
    'prefs.theme.light': 'Claro',
    'prefs.theme.dark': 'Escuro',
    'prefs.language': 'Idioma',
    // Navegação — itens
    'nav.dashboard': 'Dashboard',
    'nav.pending': 'Pendências',
    'nav.map': 'Mapa',
    'nav.programacao': 'Programação',
    'nav.unloadingPanel': 'Painel de Descarga',
    'nav.workFront': 'Frente de Trabalho',
    'nav.trips': 'Viagens',
    'nav.invoices': 'Notas Fiscais',
    'nav.tolls': 'Pedágios',
    'nav.harvest': 'Colheita',
    'nav.machineTransport': 'Transporte Máquina',
    'nav.otherTransportServices': 'Outros Serviços Transporte',
    'nav.pairs': 'Duplas',
    'nav.trucks': 'Caminhões',
    'nav.sets': 'Conjuntos',
    'nav.keyboardMessages': 'Mensagens Teclado',
    'nav.fuelRequests': 'Abastecimentos',
    'nav.transfers': 'Transferências',
    'nav.masterData': 'Cadastros',
    'nav.categories': 'Categorias',
    'nav.audit': 'Auditoria',
    'nav.lodging': 'Hospedagem',
    'nav.journeyDashboard': 'Painel de Jornada',
    'nav.dailyAllowances': 'Painel de Diárias',
    'nav.warnings': 'Gestões',
    'nav.home': 'Início',
    'nav.requests': 'Solicitações',
    'nav.loads': 'Cargas',
    'nav.contracts': 'Contratos',
    'nav.users': 'Usuários',
    'nav.fleet': 'Frota',
    'nav.places': 'Locais',
    'nav.people': 'Pessoas',
    'nav.assets': 'Ativos',
    // Navegação — seções e chrome
    'nav.section.operation': 'Operação',
    'nav.section.sets': 'Conjuntos',
    'nav.section.management': 'Gestão',
    'nav.section.logistics': 'Logística',
    'nav.section.journey': 'Jornada',
    'nav.section.general': 'Geral',
    'nav.section.administration': 'Administração',
    'nav.section.dp': 'Departamento Pessoal',
    'nav.timesheetSignature': 'Assinatura do ponto',
    'nav.dpVacations': 'Férias',
    'nav.dpTerminations': 'Desligamentos',
    'nav.dpHealthPlan': 'Plano de saúde',
    'nav.dpExams': 'Exames',
    'nav.dpLicense': 'CNH',
    'nav.dpFiles': 'Arquivos',
    'nav.dpAbsences': 'Ausências',
    'nav.dpParameters': 'Parâmetros',
    'nav.dpEmployeeHistory': 'Histórico do colaborador',
    'nav.claims': 'Sinistros',
    'nav.dpToxicology': 'Toxicológico',
    'nav.dpBonus': 'Bônus e adicionais',
    'nav.dpHiring': 'Contratações',
    'nav.dpRoutines': 'Rotinas da folha',
    'nav.dpPayrollCheck': 'Conferência da folha',
    'nav.section.dpOverview': 'Visão geral',
    'nav.section.dpTimeBenefits': 'Ponto e benefícios',
    'nav.section.dpMovements': 'Movimentações',
    'nav.section.dpHealthSafety': 'Saúde e segurança',
    'nav.section.dpDeductions': 'Descontos',
    'nav.section.fuelEntries': 'Lançamentos',
    'nav.fines': 'Multas',
    'nav.mealAllowance': 'Vale-alimentação',
    'nav.positions': 'Cargos',
    'nav.comingSoon': 'EM BREVE',
    'nav.collapse': 'Recolher menu',
    'nav.expand': 'Expandir menu',
    // Título da aba do navegador, nas telas que não estão no menu
    'tabTitle.forgotPassword': 'Esqueci a senha',
    'tabTitle.firstAccess': 'Primeiro acesso',
    'tabTitle.noAccess': 'Sem acesso',
    'tabTitle.areas': 'Áreas',
    'tabTitle.security': 'Segurança',
    // Mapa
    'map.base': 'Mapa',
    'map.satellite': 'Satélite',
    'map.style': 'Estilo do mapa',
    // Comuns (reutilizáveis em qualquer tela)
    'common.save': 'Salvar',
    'common.cancel': 'Cancelar',
    'common.delete': 'Excluir',
    'common.edit': 'Editar',
    'common.add': 'Adicionar',
    'common.new': 'Novo',
    'common.close': 'Fechar',
    'common.confirm': 'Confirmar',
    'common.back': 'Voltar',
    'common.next': 'Avançar',
    'common.search': 'Buscar',
    'common.filter': 'Filtrar',
    'common.loading': 'Carregando…',
    'common.saving': 'Salvando…',
    'common.noData': 'Sem dados',
    'common.empty': 'Nada por aqui',
    'common.error': 'Erro',
    'common.retry': 'Tentar novamente',
    'common.yes': 'Sim',
    'common.no': 'Não',
    'common.all_m': 'Todos',
    'common.all_f': 'Todas',
    'common.today': 'Hoje',
    'common.yesterday': 'Ontem',
    'common.tomorrow': 'Amanhã',
    'common.required': 'Obrigatório',
    'common.optional': 'Opcional',
    'common.actions': 'Ações',
    'common.status': 'Status',
    'common.details': 'Detalhes',
    'common.of': 'de',
  },
  en: {
    'account.settings': 'Settings',
    'account.logout': 'Sign out',
    'account.role.staff': 'Backoffice',
    'account.role.client': 'Customer',
    'prefs.theme': 'Theme',
    'prefs.theme.light': 'Light',
    'prefs.theme.dark': 'Dark',
    'prefs.language': 'Language',
    'nav.dashboard': 'Dashboard',
    'nav.pending': 'Pending items',
    'nav.map': 'Map',
    'nav.programacao': 'Schedule',
    'nav.unloadingPanel': 'Unloading Panel',
    'nav.workFront': 'Work Front',
    'nav.trips': 'Trips',
    'nav.invoices': 'Invoices',
    'nav.tolls': 'Tolls',
    'nav.harvest': 'Harvest',
    'nav.machineTransport': 'Machine Transport',
    'nav.otherTransportServices': 'Other Transport Services',
    'nav.pairs': 'Pairs',
    'nav.trucks': 'Trucks',
    'nav.sets': 'Sets',
    'nav.keyboardMessages': 'Keyboard messages',
    'nav.fuelRequests': 'Fuel',
    'nav.transfers': 'Transfers',
    'nav.masterData': 'Records',
    'nav.categories': 'Categories',
    'nav.audit': 'Audit',
    'nav.lodging': 'Lodging',
    'nav.journeyDashboard': 'Journey Panel',
    'nav.dailyAllowances': 'Daily Allowances',
    'nav.warnings': 'Measures',
    'nav.home': 'Home',
    'nav.requests': 'Requests',
    'nav.loads': 'Loads',
    'nav.contracts': 'Contracts',
    'nav.users': 'Users',
    'nav.fleet': 'Fleet',
    'nav.places': 'Places',
    'nav.people': 'People',
    'nav.assets': 'Assets',
    'nav.section.operation': 'Operations',
    'nav.section.sets': 'Sets',
    'nav.section.management': 'Management',
    'nav.section.logistics': 'Logistics',
    'nav.section.journey': 'Journey',
    'nav.section.general': 'General',
    'nav.section.administration': 'Administration',
    'nav.section.dp': 'Personnel Department',
    'nav.timesheetSignature': 'Timesheet signature',
    'nav.dpVacations': 'Vacations',
    'nav.dpTerminations': 'Terminations',
    'nav.dpHealthPlan': 'Health plan',
    'nav.dpExams': 'Medical exams',
    'nav.dpLicense': "Driver's license",
    'nav.dpFiles': 'Files',
    'nav.dpAbsences': 'Absences',
    'nav.dpParameters': 'Parameters',
    'nav.dpEmployeeHistory': 'Employee history',
    'nav.claims': 'Claims',
    'nav.dpToxicology': 'Drug testing',
    'nav.dpBonus': 'Bonuses and allowances',
    'nav.dpHiring': 'Hiring',
    'nav.dpRoutines': 'Payroll routines',
    'nav.dpPayrollCheck': 'Payroll check',
    'nav.section.dpOverview': 'Overview',
    'nav.section.dpTimeBenefits': 'Time and benefits',
    'nav.section.dpMovements': 'Staff movements',
    'nav.section.dpHealthSafety': 'Health and safety',
    'nav.section.dpDeductions': 'Deductions',
    'nav.section.fuelEntries': 'Entries',
    'nav.fines': 'Traffic fines',
    'nav.mealAllowance': 'Meal allowance',
    'nav.positions': 'Positions',
    'nav.comingSoon': 'SOON',
    'nav.collapse': 'Collapse menu',
    'nav.expand': 'Expand menu',
    'tabTitle.forgotPassword': 'Forgot password',
    'tabTitle.firstAccess': 'First access',
    'tabTitle.noAccess': 'No access',
    'tabTitle.areas': 'Areas',
    'tabTitle.security': 'Security',
    'map.base': 'Map',
    'map.satellite': 'Satellite',
    'map.style': 'Map style',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.add': 'Add',
    'common.new': 'New',
    'common.close': 'Close',
    'common.confirm': 'Confirm',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.loading': 'Loading…',
    'common.saving': 'Saving…',
    'common.noData': 'No data',
    'common.empty': 'Nothing here',
    'common.error': 'Error',
    'common.retry': 'Retry',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.all_m': 'All',
    'common.all_f': 'All',
    'common.today': 'Today',
    'common.yesterday': 'Yesterday',
    'common.tomorrow': 'Tomorrow',
    'common.required': 'Required',
    'common.optional': 'Optional',
    'common.actions': 'Actions',
    'common.status': 'Status',
    'common.details': 'Details',
    'common.of': 'of',
  },
  es: {
    'account.settings': 'Configuración',
    'account.logout': 'Cerrar sesión',
    'account.role.staff': 'Backoffice',
    'account.role.client': 'Cliente',
    'prefs.theme': 'Tema',
    'prefs.theme.light': 'Claro',
    'prefs.theme.dark': 'Oscuro',
    'prefs.language': 'Idioma',
    'nav.dashboard': 'Panel',
    'nav.pending': 'Pendientes',
    'nav.map': 'Mapa',
    'nav.programacao': 'Programación',
    'nav.unloadingPanel': 'Panel de descarga',
    'nav.workFront': 'Frente de trabajo',
    'nav.trips': 'Viajes',
    'nav.invoices': 'Notas fiscales',
    'nav.tolls': 'Peajes',
    'nav.harvest': 'Cosecha',
    'nav.machineTransport': 'Transporte de máquina',
    'nav.otherTransportServices': 'Otros servicios de transporte',
    'nav.pairs': 'Parejas',
    'nav.trucks': 'Camiones',
    'nav.sets': 'Conjuntos',
    'nav.keyboardMessages': 'Mensajes de teclado',
    'nav.fuelRequests': 'Abastecimientos',
    'nav.transfers': 'Transferencias',
    'nav.masterData': 'Registros',
    'nav.categories': 'Categorías',
    'nav.audit': 'Auditoría',
    'nav.lodging': 'Hospedaje',
    'nav.journeyDashboard': 'Panel de Jornada',
    'nav.dailyAllowances': 'Panel de Viáticos',
    'nav.warnings': 'Gestiones',
    'nav.home': 'Inicio',
    'nav.requests': 'Solicitudes',
    'nav.loads': 'Cargas',
    'nav.contracts': 'Contratos',
    'nav.users': 'Usuarios',
    'nav.fleet': 'Flota',
    'nav.places': 'Lugares',
    'nav.people': 'Personas',
    'nav.assets': 'Activos',
    'nav.section.operation': 'Operación',
    'nav.section.sets': 'Conjuntos',
    'nav.section.management': 'Gestión',
    'nav.section.logistics': 'Logística',
    'nav.section.journey': 'Jornada',
    'nav.section.general': 'General',
    'nav.section.administration': 'Administración',
    'nav.section.dp': 'Departamento de Personal',
    'nav.timesheetSignature': 'Firma de la hoja de asistencia',
    'nav.dpVacations': 'Vacaciones',
    'nav.dpTerminations': 'Desvinculaciones',
    'nav.dpHealthPlan': 'Plan de salud',
    'nav.dpExams': 'Exámenes',
    'nav.dpLicense': 'Licencia de conducir',
    'nav.dpFiles': 'Archivos',
    'nav.dpAbsences': 'Ausencias',
    'nav.dpParameters': 'Parámetros',
    'nav.dpEmployeeHistory': 'Historial del colaborador',
    'nav.claims': 'Siniestros',
    'nav.dpToxicology': 'Toxicológico',
    'nav.dpBonus': 'Bonos y adicionales',
    'nav.dpHiring': 'Contrataciones',
    'nav.dpRoutines': 'Rutinas de nómina',
    'nav.dpPayrollCheck': 'Verificación de la nómina',
    'nav.section.dpOverview': 'Visión general',
    'nav.section.dpTimeBenefits': 'Jornada y beneficios',
    'nav.section.dpMovements': 'Movimientos',
    'nav.section.dpHealthSafety': 'Salud y seguridad',
    'nav.section.dpDeductions': 'Descuentos',
    'nav.section.fuelEntries': 'Registros',
    'nav.fines': 'Multas',
    'nav.mealAllowance': 'Vale de alimentación',
    'nav.positions': 'Cargos',
    'nav.comingSoon': 'PRONTO',
    'nav.collapse': 'Contraer menú',
    'nav.expand': 'Expandir menú',
    'tabTitle.forgotPassword': 'Olvidé la contraseña',
    'tabTitle.firstAccess': 'Primer acceso',
    'tabTitle.noAccess': 'Sin acceso',
    'tabTitle.areas': 'Áreas',
    'tabTitle.security': 'Seguridad',
    'map.base': 'Mapa',
    'map.satellite': 'Satélite',
    'map.style': 'Estilo del mapa',
    'common.save': 'Guardar',
    'common.cancel': 'Cancelar',
    'common.delete': 'Eliminar',
    'common.edit': 'Editar',
    'common.add': 'Añadir',
    'common.new': 'Nuevo',
    'common.close': 'Cerrar',
    'common.confirm': 'Confirmar',
    'common.back': 'Volver',
    'common.next': 'Siguiente',
    'common.search': 'Buscar',
    'common.filter': 'Filtrar',
    'common.loading': 'Cargando…',
    'common.saving': 'Guardando…',
    'common.noData': 'Sin datos',
    'common.empty': 'Nada por aquí',
    'common.error': 'Error',
    'common.retry': 'Reintentar',
    'common.yes': 'Sí',
    'common.no': 'No',
    'common.all_m': 'Todos',
    'common.all_f': 'Todas',
    'common.today': 'Hoy',
    'common.yesterday': 'Ayer',
    'common.tomorrow': 'Mañana',
    'common.required': 'Obligatorio',
    'common.optional': 'Opcional',
    'common.actions': 'Acciones',
    'common.status': 'Estado',
    'common.details': 'Detalles',
    'common.of': 'de',
  },
}

// ── Dicionários por tela (co-locados) — coletados sem import manual ───
// Cada `*.i18n.ts` sob features/ ou shared/ exporta default `{ pt, en, es }`.
const featureModules = {
  ...import.meta.glob('../../features/**/*.i18n.ts', { eager: true }),
  ...import.meta.glob('../../shared/**/*.i18n.ts', { eager: true }),
} as Record<string, { default?: Partial<LocaleBundle> }>

function buildMessages(): LocaleBundle {
  const out: LocaleBundle = { pt: { ...core.pt }, en: { ...core.en }, es: { ...core.es } }
  const langs: Lang[] = ['pt', 'en', 'es']
  for (const mod of Object.values(featureModules)) {
    const bundle = mod?.default
    if (!bundle) continue
    for (const lang of langs) Object.assign(out[lang], bundle[lang] ?? {})
  }
  return out
}

export const messages: LocaleBundle = buildMessages()

/** Metadados dos idiomas oferecidos no seletor (rótulo nativo + bandeira). */
export const LANGUAGES: ReadonlyArray<{
  code: Lang
  label: string
  short: string
  flag: 'br' | 'us' | 'es'
}> = [
  { code: 'pt', label: 'Português', short: 'PT', flag: 'br' },
  { code: 'en', label: 'English', short: 'EN', flag: 'us' },
  { code: 'es', label: 'Español', short: 'ES', flag: 'es' },
]
